import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "./aiGenerate.css";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

export default function AIGeneratePage() {
    const [images, setImages] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [title, setTitle] = useState("");
    const [caption, setCaption] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState<string[]>([]);

    const fileRef = useRef<HTMLInputElement | null>(null);
    const navigate = useNavigate();
    
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const readers = Array.from(files).map((file) => {
            return new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(file);
            });
        });

        Promise.all(readers).then((base64Images) => {
            setImages(base64Images);
        });
    };

    const removeImage = (index: number) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const generateAll = async () => {
        if (images.length === 0) {
            setError("Add at least one image");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const res = await axios.post(`${API}/ai/generate-post`, {
                mode: "all",
                images: images.map((img) => ({
                    mime: "image/jpeg",
                    dataUrl: img,
                })),
                existing: {
                    title,
                    caption,
                    description,
                    tags,
                },
            });

            setTitle(res.data.title || "");
            setCaption(res.data.caption || "");
            setDescription(res.data.description || "");
            setTags(res.data.tags || []);
        } catch (e: any) {
            setError(e.response?.data?.detail || "AI generation failed");
        } finally {
            setLoading(false);
        }
    };

    const generateTagsOnly = async () => {
        if (images.length === 0) {
            setError("Add at least one image");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const res = await axios.post(`${API}/ai/generate-post`, {
                mode: "tags",
                images: images.map((img) => ({
                    mime: "image/jpeg",
                    dataUrl: img,
                })),
            });

            setTags(res.data.tags || []);
        } catch (e: any) {
            setError(e.response?.data?.detail || "Tag generation failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            className="ai-root"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
        >
            <div className="ai-glow" />

            <div className="ai-shell">
                {/* LEFT – IMAGE STAGE */}
                <div className="ai-left">
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept="image/*"
                        hidden
                        onChange={handleImageUpload}
                    />

                    <AnimatePresence>
                        {images.length === 0 ? (
                            <motion.div
                                className="ai-image-empty"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => fileRef.current?.click()}
                            >
                                <p>Add images</p>
                                <span>AI will understand visuals deeply</span>
                            </motion.div>
                        ) : (
                            <motion.div
                                className="ai-image-stage"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                <img src={images[0]} alt="main" className="ai-main-image" />

                                <div className="ai-thumb-row">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="ai-thumb-box">
                                            <img src={img} alt={`thumb-${idx}`} />
                                            <button onClick={() => removeImage(idx)}>×</button>
                                        </div>
                                    ))}
                                    <div
                                        className="ai-thumb-add"
                                        onClick={() => fileRef.current?.click()}
                                    >
                                        +
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* RIGHT – AI CONTENT */}
                <div className="ai-right">
                    <motion.h1
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        AI Generator
                    </motion.h1>

                    <motion.p
                        className="ai-sub"
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.35 }}
                    >
                        Let AI craft title, caption, description and tags from your visuals.
                    </motion.p>

                    <div className="ai-field">
                        <label>Title</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="AI generated title"
                        />
                    </div>

                    <div className="ai-field">
                        <label>Caption</label>
                        <textarea
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="AI generated caption"
                        />
                    </div>

                    <div className="ai-field">
                        <label>Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="AI generated description"
                        />
                    </div>

                    <div className="ai-field">
                        <label>Tags</label>
                        <div className="ai-tags-row">
                            {tags.map((t, i) => (
                                <span key={i} className="ai-tag">
                                    #{t}
                                </span>
                            ))}
                        </div>
                    </div>

                    {error && <div className="ai-error">{error}</div>}

                    <div className="ai-actions">
                        <motion.button
                            whileTap={{ scale: 0.96 }}
                            whileHover={{ scale: 1.02 }}
                            className="ai-primary"
                            onClick={generateAll}
                            disabled={loading}
                        >
                            {loading ? "Generating…" : "Generate All"}
                        </motion.button>

                        <motion.button
                            whileTap={{ scale: 0.96 }}
                            whileHover={{ scale: 1.02 }}
                            className="ai-secondary"
                            onClick={generateTagsOnly}
                            disabled={loading}
                        >
                            {loading ? "Working…" : "Tags Only"}
                        </motion.button>
                        {images.length > 0 && (caption || tags.length > 0) && (
                            <motion.button
                                whileTap={{ scale: 0.96 }}
                                whileHover={{ scale: 1.02 }}
                                className="ai-continue"
                                onClick={() =>
                                    navigate("/publish-preview", {
                                        state: {
                                            image: images[0],     // main image
                                            caption,
                                            tags,
                                            title,
                                            description,
                                        },
                                    })
                                }
                            >
                                Continue → Publish
                            </motion.button>
                        )}

                    </div>
                </div>
            </div>
        </motion.div>
    );
}
